import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle } from "lucide-react";
import { useManagerNotifications, type ManagerNotification, type ManagerNotificationPair } from "@/hooks/useManagerNotifications";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  establishmentId: string | null | undefined;
}

type CustomerLite = { id: string; first_name: string; last_name: string };
type TableLite = { id: string; name: string };

export function ManagerNotificationDialog({ establishmentId }: Props) {
  const { notifications, dismiss, isDismissing } = useManagerNotifications(establishmentId);
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState<Record<string, CustomerLite>>({});
  const [tables, setTables] = useState<Record<string, TableLite>>({});

  const current: ManagerNotification | undefined = notifications[0];

  useEffect(() => {
    if (current && !open) setOpen(true);
  }, [current?.id]);

  const pairs = useMemo(() => (current?.payload?.pairs ?? []) as ManagerNotificationPair[], [current]);

  useEffect(() => {
    if (!current) return;
    const customerIds = new Set<string>();
    const tableIds = new Set<string>();
    for (const p of pairs) {
      if (p.reservation_a.customer_id) customerIds.add(p.reservation_a.customer_id);
      if (p.reservation_b.customer_id) customerIds.add(p.reservation_b.customer_id);
      p.shared_table_ids.forEach(t => tableIds.add(t));
    }
    (async () => {
      if (customerIds.size > 0) {
        const { data } = await supabase
          .from("customers")
          .select("id, first_name, last_name")
          .in("id", Array.from(customerIds));
        const map: Record<string, CustomerLite> = {};
        for (const c of data ?? []) map[c.id] = c as CustomerLite;
        setCustomers(map);
      }
      if (tableIds.size > 0) {
        const { data } = await supabase
          .from("tables")
          .select("id, name")
          .in("id", Array.from(tableIds));
        const map: Record<string, TableLite> = {};
        for (const t of data ?? []) map[t.id] = t as TableLite;
        setTables(map);
      }
    })();
  }, [current?.id]);

  if (!current) return null;

  const customerName = (id: string | null) => {
    if (!id) return "Client inconnu";
    const c = customers[id];
    return c ? `${c.first_name} ${c.last_name}` : "…";
  };
  const tableName = (id: string) => tables[id]?.name ?? id.slice(0, 6);

  const handleDismiss = async () => {
    await dismiss(current.id);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) return; setOpen(o); }}>
      <DialogContent
        className="max-w-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <DialogTitle>{current.title}</DialogTitle>
            <Badge variant="outline" className="ml-auto">{current.severity}</Badge>
          </div>
          {current.body && (
            <DialogDescription className="pt-2 whitespace-pre-wrap">
              {current.body}
            </DialogDescription>
          )}
        </DialogHeader>

        {pairs.length > 0 && (
          <div className="border rounded-md">
            <div className="px-3 py-2 bg-muted/40 text-sm font-medium">
              {pairs.length} paire{pairs.length > 1 ? "s" : ""} concernée{pairs.length > 1 ? "s" : ""}
            </div>
            <ScrollArea className="max-h-[40vh]">
              <div className="divide-y">
                {pairs.map((p, i) => (
                  <div key={i} className="px-3 py-2 text-sm">
                    <div className="text-muted-foreground text-xs mb-1">
                      {p.reservation_a.date} • Table{p.shared_table_ids.length > 1 ? "s" : ""} : {p.shared_table_ids.map(tableName).join(", ")}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span>• {p.reservation_a.start_time.slice(0, 5)} — {customerName(p.reservation_a.customer_id)}</span>
                      <span>• {p.reservation_b.start_time.slice(0, 5)} — {customerName(p.reservation_b.customer_id)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <DialogFooter>
          <Button onClick={handleDismiss} disabled={isDismissing}>
            {isDismissing ? "…" : "J'ai compris"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
